import { Avatar, Flex } from "@daangn/carotene";
import { atoms, handleResponsiveProp } from "@daangn/carotene/css";
import { IconCheckmarkShieldFill } from "@daangn/react-monochrome-icon";
import type { Maybe } from "__generated__/jampot/alpha-ca";
import clsx from "clsx";
import { cond, constant, isNil, lt, lte, stubTrue } from "lodash";

import { match } from "ts-pattern";

import { avatarSizeMapper } from "~/design-system/components/Persona/Persona";
import { persona } from "~/design-system/components/Persona/style";
import type { GtmVariableName } from "~/utils/GtmVariable";
import { shouldUseH3LocationName } from "~/utils/shouldUseH3LocationName";
import { verifyAdminUser } from "~/utils/verifyAdminUser";

import { KarrotLocalCountryCode } from "@karrotmarket-com/core";
import type { KarrotUserProfileSummary_UserFragment } from "../../__generated__/jampot/alpha-ca";
import DefaultUserAvatar from "../assets/default_user_avatar.png";
import { buildAllPageParams } from "../core";
import { Link, useLocale, useRootLoaderData } from "../remix-lib";
import Image from "./Image";
import * as css from "./KarrotUserProfileSummary.css";

const size = {
  base: "medium",
  small: "xlarge",
  medium: "large",
  large: "xlarge",
} as const;

const classNames = persona({ size });

type ScoreColor =
  | "ice"
  | "blue"
  | "yellow"
  | "orange"
  | "pink"
  | "red"
  | "gold"
  | "platinum";

export type KarrotUserProfileSummaryGtm = {
  profileImage: GtmVariableName;
  nickname: GtmVariableName;
  region: GtmVariableName;
};
type Props = {
  karrotUser?: Maybe<KarrotUserProfileSummary_UserFragment>;
  dataGtm: KarrotUserProfileSummaryGtm;
};

/**
 * @description 글로벌 유저 프로필
 */
const KarrotUserProfileSummary = ({ karrotUser, dataGtm }: Props) => {
  const rootData = useRootLoaderData();
  const { t } = useLocale();

  const isAdminUser = verifyAdminUser({
    karrotLocalCountryCode: rootData.karrotLocalCountryCode,
    userNickname: karrotUser?.nickname ?? "",
    stage: rootData.stage,
  });

  if (isNil(karrotUser)) {
    return <WithdrawnUserProfile />;
  }

  const shouldUseScoreColor = [
    KarrotLocalCountryCode.CA,
    KarrotLocalCountryCode.US,
    KarrotLocalCountryCode.UK,
  ].includes(rootData.karrotLocalCountryCode);

  const colorVariants = cond<number, ScoreColor>([
    [(value) => lt(value, 0), constant("ice")],
    [(value) => lte(value, 9), constant("blue")],
    [(value) => lte(value, 19), constant("yellow")],
    [(value) => lte(value, 49), constant("orange")],
    [(value) => lte(value, 249), constant("pink")],
    [(value) => lte(value, 499), constant("red")],
    [(value) => lte(value, 999), constant("gold")],
    [stubTrue, constant("platinum")],
  ])(karrotUser.score);

  return (
    <Flex flexDirection="column" paddingY={4}>
      <Flex justifyContent="spaceBetween" alignItems="center">
        <div className={classNames.root}>
          <Link
            data-gtm={dataGtm.profileImage}
            href={karrotUser.id}
            aria-label={`profile page of ${karrotUser.nickname}`}
          >
            {karrotUser.isPersonaVerified ? (
              <div className={css.avatarWrap}>
                <Avatar
                  size={avatarSizeMapper(size)}
                  src={karrotUser.profileImage}
                  alt={`${karrotUser.nickname} profile`}
                  aria-hidden
                />
                <em
                  className={clsx(
                    css.avatarBadgeBase,
                    handleResponsiveProp(css.avatarBadgeMedia, size),
                  )}
                >
                  <IconCheckmarkShieldFill
                    className={clsx(
                      css.avatarBadgeIconBase,
                      handleResponsiveProp(css.avatarBadgeIconMedia, size),
                    )}
                    aria-label={`${karrotUser.nickname} is verified`}
                  />
                </em>
              </div>
            ) : (
              <Avatar
                size={avatarSizeMapper(size)}
                src={karrotUser.profileImage}
                alt={`${karrotUser.nickname} profile`}
                aria-hidden
              />
            )}
          </Link>
          <div className={classNames.content}>
            <Link
              data-gtm={dataGtm.nickname}
              href={karrotUser.id}
              className={css.link}
            >
              <span className={classNames.primaryText}>
                {karrotUser.nickname}
              </span>
            </Link>
            {match({
              isAdminUser,
              useH3LocationName: shouldUseH3LocationName(
                rootData.karrotLocalCountryCode,
              ),
            })
              .with({ isAdminUser: true }, () => null)
              .with({ useH3LocationName: true }, () => (
                <span className={classNames.secondaryText}>
                  {karrotUser.locationName?.name ?? ""}
                </span>
              ))
              .otherwise(() => (
                <Link
                  data-gtm={dataGtm.region}
                  href={
                    `/${rootData.karrotLocalCountryCode}/buy-sell/all/?` +
                    buildAllPageParams({
                      region: {
                        id: karrotUser.region.dbId,
                        name: karrotUser.region.name,
                      },
                    })
                  }
                  className={clsx(classNames.secondaryText, css.link)}
                >
                  {karrotUser.region.name}
                </Link>
              ))}
          </div>
        </div>
        <div className={css.karrotUserProfileRight}>
          <div className={css.karrotUserProfileScoreWrapper}>
            <Image
              imgClassName={css.karrotScoreImage}
              src={karrotUser.scoreSymbol}
              alt="user score"
            />
            <span
              className={clsx(
                css.karrotUserProfileScoreBase,
                shouldUseScoreColor
                  ? css.karrotUserProfileScoreColor({ color: colorVariants })
                  : undefined,
              )}
            >
              {karrotUser.score > 999 ? "1000+" : karrotUser.score}
              {rootData.karrotLocalCountryCode === "jp" && <span>℃</span>}
            </span>
          </div>
          <span className={css.karrotUserReviewCount}>
            {t("KarrotUserProfileSummary.n_reviews", {
              smart_count: karrotUser.reviewCount,
            })}
          </span>
        </div>
      </Flex>
    </Flex>
  );
};

export default KarrotUserProfileSummary;

const WithdrawnUserProfile = () => {
  const { t } = useLocale();

  return (
    <Flex flexDirection="column" paddingY={4}>
      <Flex justifyContent="spaceBetween" alignItems="center">
        <div className={classNames.root}>
          <Avatar
            size={avatarSizeMapper(size)}
            src={DefaultUserAvatar}
            alt=""
          />
          <div className={classNames.content}>
            <div className={classNames.primaryText}>
              <span
                className={atoms({
                  color: "placeholder",
                })}
              >
                {t("KarrotUserProfileSummary.withdrawn_user")}
              </span>
            </div>
          </div>
        </div>
      </Flex>
    </Flex>
  );
};
